import { useMemo, useState } from 'react'

import rulesJson from '../../demo/fixtures/w3/rules.json'
import { selectAutopilotDefaults } from '../../demo/selectors'
import type { AutopilotGoal, AutopilotState, Campaign } from '../../demo/types'
import { Button, Field, Input, Modal } from '../../ui'
import { formatTargetInput, parseTarget, targetForGoal } from './autopilot'
import styles from './AutopilotSection.module.css'

interface GoalCopy {
  key: AutopilotGoal
  label: string
  fieldLabel: string
  unit: string
  prefix: string
}

interface RulesFixture {
  goals: GoalCopy[]
  targetImproveRatio: number
}

interface AutopilotDialogProps {
  open: boolean
  campaign: Campaign
  autopilot: AutopilotState | null
  copy: { title: string; sub: string; goalLabel: string; cancel: string; ok: string }
  onClose: () => void
  onConfirm: (input: { goal: AutopilotGoal; target: number }) => void
}

const rules = rulesJson as unknown as RulesFixture

export function AutopilotDialog({ open, campaign, autopilot, copy, onClose, onConfirm }: AutopilotDialogProps) {
  const defaults = useMemo(() => selectAutopilotDefaults(campaign.id), [campaign])
  const initialGoal = autopilot?.goal ?? defaults?.goal ?? 'more_reach'
  const initialTarget = autopilot?.target ?? defaults?.target ?? null
  const [goal, setGoal] = useState<AutopilotGoal>(initialGoal)
  const [target, setTarget] = useState(formatTargetInput(initialTarget))

  const selected = rules.goals.find((item) => item.key === goal) ?? rules.goals[0]!
  const parsedTarget = parseTarget(target)
  const chooseGoal = (next: AutopilotGoal) => {
    setGoal(next)
    setTarget(formatTargetInput(targetForGoal(campaign, next, rules.targetImproveRatio)))
  }

  return (
    <div className={styles.dialogScope} data-scope={open ? 'ap-dialog' : undefined}>
      <Modal
        open={open}
        title={copy.title}
        onClose={onClose}
        footer={<><Button variant="ghost" onClick={onClose}>{copy.cancel}</Button><Button disabled={parsedTarget === null} onClick={() => { if (parsedTarget !== null) onConfirm({ goal, target: parsedTarget }) }}>{copy.ok}</Button></>}
      >
        <p className={styles.dialogHint}>{copy.sub}</p>
        <p className={styles.goalLabel}>{copy.goalLabel}</p>
        <div className={styles.goalGrid}>
          {rules.goals.map((item) => <button type="button" key={item.key} className={`${styles.goalChoice} ${item.key === goal ? styles.goalChoiceOn : ''}`} aria-pressed={item.key === goal} onClick={() => chooseGoal(item.key)}><span>{item.label}</span><small>{item.fieldLabel}</small></button>)}
        </div>
        <Field label={selected.fieldLabel} auxiliary="默认按当前值改善 20% 推出，可以改" className={styles.targetField}>
          <Input
            className={selected.prefix ? styles.prefixInput : ''}
            inputMode="decimal"
            value={target}
            icon={<span>{selected.prefix || selected.unit}</span>}
            onChange={(event) => setTarget(event.target.value)}
          />
        </Field>
      </Modal>
    </div>
  )
}
